import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  useTheme as useMuiTheme,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RedoIcon from '@mui/icons-material/Redo';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  FunnelChart,
  Funnel,
  LabelList,
} from 'recharts';
import {
  fetchDashboardSummary,
  fetchAgingData,
  fetchWATypeDistribution,
  fetchEngagementTodaySummary,
  fetchWhatsAppCloudStatus,
  fetchWhatsAppCloudSpend,
  CloudSpendResponse,
  DashboardSummary,
  AgingData,
  EngagementTodaySummary,
  STAGE_LABELS,
  STAGE_COLORS,
  Stage,
} from '../services/api';
import { useNavigate } from 'react-router-dom';

const ACCENT = '#ed8936';

function formatSgd(n: number): string {
  return `S$${Number(n || 0).toFixed(2)}`;
}

function sgDateKey(d = new Date()): string {
  const SGT = 8 * 60 * 60 * 1000;
  return new Date(d.getTime() + SGT).toISOString().slice(0, 10);
}

function monthToDateKeys(now = new Date()): string[] {
  const SGT = 8 * 60 * 60 * 1000;
  const sgt = new Date(now.getTime() + SGT);
  const y = sgt.getUTCFullYear();
  const m = sgt.getUTCMonth();
  const today = sgt.getUTCDate();
  const keys: string[] = [];
  for (let day = 1; day <= today; day++) {
    keys.push(new Date(Date.UTC(y, m, day)).toISOString().slice(0, 10));
  }
  return keys;
}

// 7-Day Engagement execution statuses, in display order. Status colors are
// validated (dataviz six-checks, light+dark) and always paired with an icon +
// label + count — never color alone.
const ENGAGEMENT_STATUSES = [
  { key: 'sent', label: 'Sent', color: '#15803d', icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> },
  { key: 'sending', label: 'Sending', color: '#b45309', icon: <AutorenewIcon sx={{ fontSize: 14 }} /> },
  { key: 'pending', label: 'Pending', color: '#3b82f6', icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
  { key: 'failed', label: 'Failed', color: '#dc2626', icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} /> },
  { key: 'skipped', label: 'Skipped', color: '#7c3aed', icon: <RedoIcon sx={{ fontSize: 14 }} /> },
] as const;

type EngagementStatusKey = typeof ENGAGEMENT_STATUSES[number]['key'];

// Recharts tooltips default to dark text, which disappears on our dark surface.
// Share one set of dark-mode-safe styles across every chart tooltip.
const TOOLTIP_CONTENT_STYLE = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' };
const TOOLTIP_LABEL_STYLE = { color: 'var(--color-text-strong)' };
const TOOLTIP_ITEM_STYLE = { color: 'var(--color-text-strong)' };

// Recharts renders ticks as <text fill="..."> SVG attributes which do NOT resolve
// CSS custom properties like var(--color-text-subtle), so the labels fall back to
// the SVG default (black) and are invisible in dark mode. Resolve to concrete
// colors via the MUI theme palette instead.
function useChartStyles() {
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  return useMemo(
    () => ({
      tickFill: muiTheme.palette.text.secondary,
      axisStroke: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(31,41,68,0.24)',
      gridStroke: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(31,41,68,0.12)',
      polarGridStroke: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(31,41,68,0.18)',
    }),
    [muiTheme.palette.text.secondary, isDark]
  );
}

function StatCard({ title, value, icon, color, onClick }: { title: string; value: string | number; icon: React.ReactNode; color?: string; onClick?: () => void }) {
  return (
    <Card
      sx={{
        bgcolor: 'var(--color-surface)',
        height: '100%',
        ...(onClick ? { cursor: 'pointer', '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.05)' } } : {}),
      }}
      onClick={onClick}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color: color || ACCENT, fontSize: 40, display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>{value}</Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>{title}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Dashboard({ adminMode = false }: { adminMode?: boolean }) {
  const navigate = useNavigate();
  const chartStyles = useChartStyles();
  const tickStyle = useMemo(() => ({ fontSize: 11, fill: chartStyles.tickFill }), [chartStyles.tickFill]);
  const tickStyleSmall = useMemo(() => ({ fontSize: 10, fill: chartStyles.tickFill }), [chartStyles.tickFill]);
  const tickStyleTiny = useMemo(() => ({ fontSize: 9, fill: chartStyles.tickFill }), [chartStyles.tickFill]);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [aging, setAging] = useState<AgingData | null>(null);
  const [waDistribution, setWaDistribution] = useState<Array<{ type: string; count: string }>>([]);
  const [engagementToday, setEngagementToday] = useState<EngagementTodaySummary | null>(null);
  // Drill-in dialog for today's engagement queue; statusFilter '' = all rows.
  const [engagementDetailOpen, setEngagementDetailOpen] = useState(false);
  const [engagementStatusFilter, setEngagementStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [cloudSpend, setCloudSpend] = useState<CloudSpendResponse | null>(null);
  const [showCloudSpend, setShowCloudSpend] = useState(false);
  const [spendRange, setSpendRange] = useState<'today' | 'month'>('month');

  useEffect(() => {
    Promise.all([
      fetchDashboardSummary(adminMode),
      fetchAgingData(adminMode),
      fetchWATypeDistribution(adminMode),
      fetchEngagementTodaySummary(adminMode).catch(() => null),
      fetchWhatsAppCloudStatus().catch(() => null),
    ]).then(([s, a, d, e, cloud]) => {
      setSummary(s);
      setAging(a);
      setWaDistribution(d);
      setEngagementToday(e);
      const cloudOn = cloud?.provider === 'cloud' && !!cloud.account?.connected;
      setShowCloudSpend(cloudOn);
      if (cloudOn) {
        return fetchWhatsAppCloudSpend()
          .then(setCloudSpend)
          .catch(() => setCloudSpend(null));
      }
      setCloudSpend(null);
      return undefined;
    }).catch(console.error).finally(() => setLoading(false));
  }, [adminMode]);

  const spendByDate = useMemo(() => {
    const map = new Map<string, number>();
    cloudSpend?.overall.daily.forEach((d) => map.set(d.date, d.cost));
    return map;
  }, [cloudSpend]);

  const openEngagementDetail = (status: string) => {
    setEngagementStatusFilter(status);
    setEngagementDetailOpen(true);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (!summary) {
    return <Typography color="error">Failed to load dashboard</Typography>;
  }

  const pieData = summary.stageCounts.map((s) => ({
    name: STAGE_LABELS[s.stage as Stage] || s.stage,
    value: parseInt(s.count, 10),
    color: STAGE_COLORS[s.stage as Stage] || '#888',
  }));

  const funnelData = ['new', 'contacted', 'viewing', 'negotiation', 'won']
    .map((stage) => {
      const found = summary.stageCounts.find((s) => s.stage === stage);
      return {
        name: STAGE_LABELS[stage as Stage] || stage,
        value: found ? parseInt(found.count, 10) : 0,
        fill: STAGE_COLORS[stage as Stage] || '#888',
      };
    });

  const radarData = waDistribution.map((d) => ({
    subject: d.type || 'Other',
    value: parseInt(d.count, 10),
  }));

  const hypingChartData = (summary.hypingProjectCounts || []).map((h) => ({
    name: h.project,
    count: parseInt(h.count, 10),
  }));

  const agingChartData = aging ? Object.entries(aging).map(([bucket, clients]) => ({
    bucket,
    count: clients.length,
  })) : [];

  const engagementTotal = engagementToday
    ? Object.values(engagementToday.counts).reduce((a, b) => a + b, 0)
    : 0;
  const engagementDetailRows = (engagementToday?.executions || []).filter(
    (e) => !engagementStatusFilter || e.status === engagementStatusFilter,
  );

  const todayKey = sgDateKey();
  const todaySpend = spendByDate.get(todayKey) ?? 0;
  const monthKeys = monthToDateKeys();
  const monthChartData = monthKeys.map((date) => ({
    date: date.slice(5),
    cost: spendByDate.get(date) ?? 0,
  }));
  const monthTotal = monthChartData.reduce((s, d) => s + d.cost, 0);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Dashboard</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Clients" value={summary.totalClients} icon={<PeopleIcon fontSize="inherit" />} onClick={() => navigate('/super-connector/clients')} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="WA Messages Sent" value={summary.totalWA} icon={<WhatsAppIcon fontSize="inherit" />} color="#25D366" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="In Pipeline"
            value={summary.stageCounts.filter((s) => !['won', 'lost'].includes(s.stage)).reduce((a, b) => a + parseInt(b.count, 10), 0)}
            icon={<TrendingUpIcon fontSize="inherit" />}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Won"
            value={summary.stageCounts.find((s) => s.stage === 'won')?.count || '0'}
            icon={<TrendingUpIcon fontSize="inherit" />}
            color="#22c55e"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* 7-Day Engagement — today's send queue */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'var(--color-surface)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)' }}>
                  7-Day Engagement · Today
                </Typography>
                {engagementToday && engagementTotal > 0 && (
                  <Button size="small" onClick={() => openEngagementDetail('')} sx={{ textTransform: 'none', color: ACCENT }}>
                    View details
                  </Button>
                )}
              </Box>
              {!engagementToday || engagementTotal === 0 ? (
                <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', py: 2, textAlign: 'center' }}>
                  No 7-day engagement messages queued or sent today
                </Typography>
              ) : (
                <>
                  {/* Count row: icon + label + count per status, clickable to drill in */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    {ENGAGEMENT_STATUSES.map(({ key, label, color, icon }) => {
                      const count = engagementToday.counts[key] ?? 0;
                      if (count === 0 && (key === 'sending' || key === 'skipped')) return null;
                      return (
                        <Chip
                          key={key}
                          icon={<Box sx={{ display: 'flex', color: `${color} !important` }}>{icon}</Box>}
                          label={`${label} ${count}`}
                          size="small"
                          onClick={() => openEngagementDetail(key)}
                          sx={{
                            bgcolor: `${color}18`,
                            color: 'var(--color-text-strong)',
                            fontWeight: 600,
                            border: `1px solid ${color}55`,
                          }}
                        />
                      );
                    })}
                  </Box>
                  {/* Proportion bar: one segment per status, 2px surface gaps */}
                  <Box sx={{ display: 'flex', gap: '2px', height: 14, borderRadius: 1, overflow: 'hidden' }}>
                    {ENGAGEMENT_STATUSES.map(({ key, label, color }) => {
                      const count = engagementToday.counts[key] ?? 0;
                      if (count === 0) return null;
                      return (
                        <MuiTooltip key={key} title={`${label}: ${count} of ${engagementTotal}`}>
                          <Box
                            onClick={() => openEngagementDetail(key)}
                            sx={{ flex: count, bgcolor: color, borderRadius: '3px', cursor: 'pointer', minWidth: 8 }}
                          />
                        </MuiTooltip>
                      );
                    })}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Clients Hyped by Project */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'var(--color-surface)', height: 320 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>Clients Hyped by Project</Typography>
              {hypingChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={hypingChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartStyles.gridStroke} />
                    <XAxis type="number" allowDecimals={false} stroke={chartStyles.axisStroke} tick={tickStyle} />
                    <YAxis type="category" dataKey="name" stroke={chartStyles.axisStroke} tick={tickStyle} width={160} />
                    <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                    <Bar dataKey="count" name="Clients" fill={ACCENT} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
                  <Typography variant="body2" sx={{ color: 'var(--color-text-faint)' }}>No hyping projects yet</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Client Hype Trend */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'var(--color-surface)', height: 320 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>Clients Hyped Over Time</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={summary.clientTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartStyles.gridStroke} />
                  <XAxis dataKey="date" stroke={chartStyles.axisStroke} tick={tickStyleSmall} />
                  <YAxis stroke={chartStyles.axisStroke} tick={tickStyleSmall} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                  <Line type="monotone" dataKey="count" stroke={ACCENT} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* WA Messages Trend */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'var(--color-surface)', height: 320 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>WA Messages Sent Over Time</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={summary.waTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartStyles.gridStroke} />
                  <XAxis dataKey="date" stroke={chartStyles.axisStroke} tick={tickStyleSmall} />
                  <YAxis stroke={chartStyles.axisStroke} tick={tickStyleSmall} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                  <Line type="monotone" dataKey="count" stroke="#25D366" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {showCloudSpend && (
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'var(--color-surface)', height: 340 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)' }}>
                    Official API spend (SGD)
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={spendRange}
                    onChange={(_, v: 'today' | 'month' | null) => { if (v) setSpendRange(v); }}
                    sx={{
                      '& .MuiToggleButton-root': { textTransform: 'none', px: 1.25, py: 0.25, fontSize: '0.7rem' },
                      '& .Mui-selected': { color: '#ed8936 !important', bgcolor: 'rgba(237,137,54,0.15) !important' },
                    }}
                  >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="month">This month</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 1 }}>
                  Estimated at S$0.10 per Official API marketing message · not Meta billed amount
                </Typography>
                {spendRange === 'today' ? (
                  <Box sx={{ height: 230, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h4" fontWeight={800} sx={{ color: '#16a34a' }}>
                      {formatSgd(todaySpend)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                      Estimated spend today
                    </Typography>
                    {!cloudSpend && (
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                        Spend data unavailable
                      </Typography>
                    )}
                  </Box>
                ) : monthChartData.every((d) => d.cost === 0) && !cloudSpend ? (
                  <Box sx={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-faint)' }}>No spend yet</Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 0.5 }}>
                      Month to date · {formatSgd(monthTotal)} est.
                    </Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={monthChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartStyles.gridStroke} />
                        <XAxis dataKey="date" stroke={chartStyles.axisStroke} tick={tickStyleTiny} />
                        <YAxis stroke={chartStyles.axisStroke} tick={tickStyleSmall} tickFormatter={(v) => `$${Number(v).toFixed(0)}`} />
                        <Tooltip
                          contentStyle={TOOLTIP_CONTENT_STYLE}
                          labelStyle={TOOLTIP_LABEL_STYLE}
                          itemStyle={TOOLTIP_ITEM_STYLE}
                          formatter={(value: number) => [formatSgd(value), 'Est. spend']}
                        />
                        <Line type="monotone" dataKey="cost" stroke="#16a34a" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Pipeline Pie */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'var(--color-surface)', height: 320 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>Pipeline Distribution</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {pieData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Funnel */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'var(--color-surface)', height: 320 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>Pipeline Funnel</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={chartStyles.gridStroke} />
                  <XAxis type="number" stroke={chartStyles.axisStroke} tick={tickStyleSmall} />
                  <YAxis type="category" dataKey="name" stroke={chartStyles.axisStroke} tick={tickStyle} width={90} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {funnelData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* WA Type Spider */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'var(--color-surface)', height: 320 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>Top WA Message Types</Typography>
              {radarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={chartStyles.polarGridStroke} />
                    <PolarAngleAxis dataKey="subject" tick={tickStyleSmall} />
                    <PolarRadiusAxis tick={tickStyleTiny} />
                    <Radar dataKey="value" stroke={ACCENT} fill={ACCENT} fillOpacity={0.3} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
                  <Typography variant="body2" sx={{ color: 'var(--color-text-faint)' }}>No data yet</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Aging */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'var(--color-surface)' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>Client Aging (Days Since Last Activity)</Typography>
              <Grid container spacing={1}>
                {agingChartData.map((item) => (
                  <Grid item xs={12} sm={6} md key={item.bucket}>
                    <Card
                      sx={{
                        bgcolor: 'var(--color-bg)',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.05)' },
                      }}
                      onClick={() => {
                        // Navigate to clients filtered by this aging bucket
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ color: item.count > 0 ? ACCENT : 'var(--color-text-faint)' }}>
                          {item.count}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>
                          {item.bucket} days
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {aging && Object.values(aging).some((arr) => arr.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={agingChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartStyles.gridStroke} />
                      <XAxis dataKey="bucket" stroke={chartStyles.axisStroke} tick={tickStyle} />
                      <YAxis stroke={chartStyles.axisStroke} tick={tickStyleSmall} />
                      <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                      <Bar dataKey="count" fill={ACCENT} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Drill-in: today's engagement queue, row per execution */}
      <Dialog
        open={engagementDetailOpen}
        onClose={() => setEngagementDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
          7-Day Engagement · Today
          <Select
            size="small"
            value={engagementStatusFilter}
            onChange={(e) => setEngagementStatusFilter(e.target.value)}
            displayEmpty
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {ENGAGEMENT_STATUSES.map(({ key, label }) => (
              <MenuItem key={key} value={key}>{label} ({engagementToday?.counts[key] ?? 0})</MenuItem>
            ))}
          </Select>
        </DialogTitle>
        <DialogContent>
          {engagementDetailRows.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', py: 3, textAlign: 'center' }}>
              No messages with this status today
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell align="center">Day</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {engagementDetailRows.map((row) => {
                  const meta = ENGAGEMENT_STATUSES.find((s) => s.key === (row.status as EngagementStatusKey));
                  const when = row.sentAt || row.updatedAt;
                  return (
                    <TableRow key={row.executionId} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          onClick={() => navigate(`/super-connector/clients/${row.clientId}`)}
                          sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { color: ACCENT } }}
                        >
                          {row.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">Day {row.dayNumber}</TableCell>
                      <TableCell>
                        <Chip
                          icon={meta ? <Box sx={{ display: 'flex', color: `${meta.color} !important` }}>{meta.icon}</Box> : undefined}
                          label={meta?.label || row.status}
                          size="small"
                          sx={{
                            height: 22,
                            bgcolor: `${meta?.color || '#888'}18`,
                            color: 'var(--color-text-strong)',
                            fontWeight: 600,
                            border: `1px solid ${meta?.color || '#888'}55`,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.status === 'sent' && when ? new Date(when).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#dc2626', maxWidth: 260 }}>
                        {row.lastError || ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEngagementDetailOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
